import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { $convertFromMarkdownString } from '@lexical/markdown';
import { MARKDOWN_TRANSFORMERS } from './plugins/MarkdownTransformers';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { EditorState, LexicalEditor } from 'lexical';
import React, { useEffect, useRef } from 'react';

import ToolbarPlugin from './plugins/ToolbarPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';

import './index.css';
import EditorTheme from './theme';
import classNames from 'classnames';

// AutoLink matchers for URLs and emails
const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const EMAIL_MATCHER =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

const MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text);
    return (
      match && {
        index: match.index,
        length: match[0].length,
        text: match[0],
        url: match[0],
      }
    );
  },
  (text: string) => {
    const match = EMAIL_MATCHER.exec(text);
    return (
      match && {
        index: match.index,
        length: match[0].length,
        text: match[0],
        url: `mailto:${match[0]}`,
      }
    );
  },
];

type EditorProps = {
  innerRef?: React.MutableRefObject<LexicalEditor | null>;
  readOnly?: boolean;
  initialContentMarkdown?: string;
  placeholder?: string;
  elKey?: string;
  className?: string;
  allowLinks?: boolean;
  contentClassName?: string;
  containerClassName?: string;
  onError?: (error: Error) => never | void;
  onChange?: (editorState: EditorState, editor: LexicalEditor) => void;
};

// Using a helper to access the editor which requires the LexicalComposerContext provided by the LexicalComposer
// So `editor` can only be accessed from inside the LexicalComposer element
function EditorHelper({ innerRef, readOnly = false }: EditorProps) {
  const [editor] = useLexicalComposerContext();

  // Set the reference when the editor is mounted
  useEffect(() => {
    if (innerRef) innerRef.current = editor;
  }, [editor, readOnly]);

  // After an editor is created, the readOnly mode can only be changed imperatively
  // See https://lexical.dev/docs/concepts/read-only
  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  return null;
}

export default function Editor({
  initialContentMarkdown,
  placeholder = '',
  className = '',
  contentClassName = '',
  containerClassName = '',
  elKey = 'lexical',
  onChange = () => null,
  allowLinks = true,
  ...props
}: EditorProps) {
  const onError = props.onError
    ? props.onError
    : (error: Error) => {
        throw error;
      };

  // Capture initial content only on first mount or when elKey changes
  const initialContentRef = useRef<string | undefined>(initialContentMarkdown);
  const currentElKeyRef = useRef(elKey);

  // Only update initial content when elKey changes (template applied)
  if (currentElKeyRef.current !== elKey) {
    initialContentRef.current = initialContentMarkdown;
    currentElKeyRef.current = elKey;
  }

  const initialConfig = {
    namespace: 'lexical-editor',
    theme: EditorTheme,
    onError,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      HorizontalRuleNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
    ],
    editorState: initialContentRef.current
      ? () =>
          $convertFromMarkdownString(
            initialContentRef.current!,
            MARKDOWN_TRANSFORMERS,
            undefined,
            true,
          )
      : undefined,
  };

  return (
    <div key={elKey} className={containerClassName}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className={classNames('editor editor-container', className)}>
          {!props.readOnly && (
            <ToolbarPlugin
              showJustificationButtons={true}
              allowLinks={allowLinks}
            />
          )}
          <div className="editor-inner" id="editor-container">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={classNames('editor-input', contentClassName)}
                />
              }
              placeholder={
                <div className="editor-placeholder">{placeholder}</div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ClearEditorPlugin />
            <HistoryPlugin />
            <HorizontalRulePlugin />
            <ListPlugin />
            <TabIndentationPlugin />
            <LinkPlugin />
            <AutoLinkPlugin matchers={MATCHERS} />
            {!props.readOnly && <FloatingLinkEditorPlugin />}
            <OnChangePlugin onChange={onChange} ignoreSelectionChange={true} />
            <EditorHelper {...props} />
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
}
